import { useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { MapPin, X } from "lucide-react";

export function AddressAutocomplete({ value, onChange, onLocationSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  const searchAddress = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await fetch(
  `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
);
      const data = await res.json();
      setSuggestions(data);
      setOpen(true);
    } catch (e) {
      console.error(e);
      toast.error("Search failed");
    }
  };

  const handleInputChange = (val) => {
    onChange(val);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => searchAddress(val), 500);
  };

  const handleSelect = (suggestion) => {
    const address = suggestion.display_name;
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);

    onChange(address);
    onLocationSelect({ latitude: lat, longitude: lng, address });
    setSuggestions([]);
    setOpen(false);
    toast.success("Location selected!");
  };

  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Search address (e.g., Av. Paulista, São Paulo)..."
          className="rounded-sm pl-9"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 border border-border bg-card rounded-sm mt-1 max-h-60 overflow-y-auto z-50 shadow-lg">
          {suggestions.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors border-b border-border last:border-0 text-sm"
            >
              <div className="font-medium text-foreground">
                {s.display_name.split(",")[0]}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {s.display_name.split(",").slice(1, 3).join(", ")}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && suggestions.length === 0 && value.length >= 3 && (
        <div className="absolute top-full left-0 right-0 border border-border bg-card rounded-sm mt-1 px-4 py-3 z-50 text-sm text-muted-foreground">
          No results found. Try another search.
        </div>
      )}
    </div>
  );
}